import { filesApi } from '@/features/files/filesApi';
import {
	buildPolicySummaryLine,
	buildPolicyTransactionSummaryLine,
	getSortDate,
	isEmailSharepointFile,
	stripHtml,
	toJsonText,
	truncate,
} from './helpers';
import { useDispatch, useSelector } from 'react-redux';
import { useListNotesQuery } from '@/features/engagements/engagementsApi';
import { useGetWorkspacePageQuery } from '@/features/workspaces/workspacesApi';
import { useGetClientPoliciesQuery } from '@/features/clients/clientsApi';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import { useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { AI_SUMMARY_TYPES, useGetAIDocsQuery } from '@/features/factFind/factFindApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useGetAIMessages from '@/hooks/useGetAIMessages';
import { snakeCase, startCase } from 'lodash';

const makeSource = ({ id, title, type, content, snippet, url = null, metadata = {} }) => ({
	id,
	title,
	type,
	content,
	snippet: snippet ?? truncate(content, 240),
	url,
	metadata,
});

const buildNoteSources = ({ notes = [], client, deal }) =>
	notes.slice(0, 12).map((note, index) => {
		const content = truncate(stripHtml(note?.htmlBody ?? ''), 3000);

		return makeSource({
			id: `note-${note.id ?? index + 1}`,
			title: note?.subject ?? `Note ${index + 1}`,
			type: 'note',
			content,
			metadata: {
				noteId: note.id ?? null,
				clientId: client?.clientId ?? null,
				dealId: deal?.dealId ?? null,
				subject: note?.subject ?? null,
			},
		});
	});

const buildClientPolicySources = ({
	clientPolicies = [],
	clientPolicyTransactions = [],
	client,
}) => {
	const sources = [];

	const policyLines = clientPolicies
		.map((policy) => buildPolicySummaryLine(policy))
		.filter(Boolean)
		.slice(0, 20);

	if (policyLines.length > 0) {
		const content = truncate(policyLines.join('\n'), 4000);

		sources.push(
			makeSource({
				id: `client-policies-${client?.clientId ?? 'current'}`,
				title: 'Client Policies',
				type: 'policy',
				content,
				metadata: {
					clientId: client?.clientId ?? null,
					insightId: client?.insightId ?? null,
					policyCount: clientPolicies.length,
				},
			}),
		);
	}

	const transactionLines = clientPolicyTransactions
		.map((transaction) => buildPolicyTransactionSummaryLine(transaction))
		.filter(Boolean)
		.slice(0, 20);

	if (transactionLines.length > 0) {
		const content = truncate(transactionLines.join('\n'), 4000);

		sources.push(
			makeSource({
				id: `client-policy-transactions-${client?.clientId ?? 'current'}`,
				title: 'Client Policy Transactions',
				type: 'policy_transaction',
				content,
				metadata: {
					clientId: client?.clientId ?? null,
					insightId: client?.insightId ?? null,
					transactionCount: clientPolicyTransactions.length,
				},
			}),
		);
	}

	return sources;
};

const buildWorkspaceSources = ({ workspacePage, contextType, objectId, client, deal }) => {
	if (!workspacePage?.html) return [];

	const content = truncate(stripHtml(workspacePage.html), 4000);
	if (!content) return [];

	return [
		makeSource({
			id: `${contextType}-workspace-${objectId}`,
			title: `Workspace ${deal?.dealName ?? client?.name ?? ''}`.trim(),
			type: 'workspace_item',
			content,
			metadata: {
				...(deal?.dealId
					? { dealId: deal.dealId }
					: { clientId: client?.clientId ?? null }),
			},
		}),
	];
};

const buildDealAiSources = ({ aiSummaries, deal }) => {
	const sources = [];

	for (const summaryType of [AI_SUMMARY_TYPES.riskInfoSummary, AI_SUMMARY_TYPES.quoteSummary]) {
		const latestDoc = aiSummaries?.[summaryType]?.[0];
		if (latestDoc?.message) {
			const content = truncate(stripHtml(latestDoc.message), 4000);

			const title = startCase(summaryType);
			const type = snakeCase(title);
			const idTitle = type.replaceAll('_', '-');
			sources.push(
				makeSource({
					id: `${idTitle}-${latestDoc.id ?? 'latest'}`,
					title,
					type,
					content,
					metadata: {
						dealId: deal?.dealId ?? null,
						summaryType,
					},
				}),
			);
		}
	}

	return sources;
};

const buildDealPolicySources = ({ policyData, deal }) => {
	const sources = [];
	const dealPolicies = policyData?.policies ?? [];
	const dealTransactions = policyData?.transactions ?? [];

	if (dealPolicies.length > 0) {
		const content = truncate(
			dealPolicies
				.map((policy) => buildPolicySummaryLine(policy))
				.filter(Boolean)
				.slice(0, 20)
				.join('\n'),
			4000,
		);

		sources.push(
			makeSource({
				id: `deal-policies-${deal?.dealId ?? 'current'}`,
				title: 'Deal Policies',
				type: 'policy',
				content,
				metadata: {
					dealId: deal?.dealId ?? null,
					policyCount: dealPolicies.length,
				},
			}),
		);
	}

	if (dealTransactions.length > 0) {
		const content = truncate(
			dealTransactions
				.map((transaction) => buildPolicyTransactionSummaryLine(transaction))
				.filter(Boolean)
				.slice(0, 20)
				.join('\n'),
			4000,
		);

		sources.push(
			makeSource({
				id: `deal-policy-transactions-${deal?.dealId ?? 'current'}`,
				title: 'Deal Policy Transactions',
				type: 'policy_transaction',
				content,
				metadata: {
					dealId: deal?.dealId ?? null,
					transactionCount: dealTransactions.length,
				},
			}),
		);
	}

	if (dealPolicies.length > 0 || dealTransactions.length > 0) {
		sources.push(
			makeSource({
				id: `deal-policies-raw-${deal?.dealId ?? 'current'}`,
				title: 'Deal Policies Structured Data',
				type: 'policy_data',
				content: toJsonText(
					{
						policies: dealPolicies.slice(0, 10),
						transactions: dealTransactions.slice(0, 10),
					},
					4000,
				),
				snippet: truncate(
					`${dealPolicies.length} policies | ${dealTransactions.length} transactions`,
					240,
				),
				metadata: {
					dealId: deal?.dealId ?? null,
				},
			}),
		);
	}

	return sources;
};

const splitSharepointFiles = (files = []) => {
	const latestFiles = [...files].sort((a, b) => getSortDate(b) - getSortDate(a)).slice(0, 12);

	return latestFiles.reduce(
		(acc, file) => {
			if (isEmailSharepointFile(file)) {
				acc.emailFiles.push(file);
			} else {
				acc.documentFiles.push(file);
			}

			return acc;
		},
		{ emailFiles: [], documentFiles: [] },
	);
};

const buildSharepointDocumentSources = (files = []) =>
	files.map((file, index) => {
		const mimeType = file?.file?.mimeType ?? '';
		const content = truncate(
			[
				file?.name,
				file?.description,
				file?.metadata?._ExtendedDescription,
				file?.sentDate,
				file?.fromEmail,
				file?.toEmail,
			]
				.filter(Boolean)
				.join(' | '),
			2200,
		);

		return makeSource({
			id: `sharepoint-file-${file.id ?? index + 1}`,
			title: file?.name ?? `SharePoint file ${index + 1}`,
			type: 'sharepoint_document',
			content,
			snippet: truncate(content || file?.name || '', 240),
			url: file?.webUrl ?? file?.listItem?.webUrl ?? null,
			metadata: {
				fileId: file?.id ?? null,
				mimeType,
				driveId: file?.parentReference?.driveId ?? null,
			},
		});
	});

const getRecipientList = (preview) => {
	if (!Array.isArray(preview?.recipients)) return null;

	return preview.recipients
		.map((recipient) => recipient?.smtpAddress ?? recipient?.email ?? '')
		.filter(Boolean)
		.join(', ');
};

const buildEmailFallbackContent = ({ file, preview = null }) =>
	truncate(
		[
			preview?.subject,
			preview?.senderEmail ?? preview?.senderSmtpAddress,
			getRecipientList(preview),
			file?.name,
			file?.description,
			file?.metadata?._ExtendedDescription,
			file?.sentDate,
			file?.fromEmail,
			file?.toEmail,
		]
			.filter(Boolean)
			.join(' | '),
		2200,
	);

const buildSharepointEmailSource = async ({
	file,
	dispatch,
	sharepointItemId,
	sharepointDriveId,
}) => {
	try {
		const preview = await dispatch(
			filesApi.endpoints.getMsgFileHtml.initiate({
				id: file.id,
				downloadUrl: file['@microsoft.graph.downloadUrl'],
				driveId: sharepointItemId,
				clientFolderDriveId: sharepointDriveId,
				createdDateTime: file.createdDateTime,
			}),
		).unwrap();

		const previewText = truncate(stripHtml(preview?.html ?? preview?.body ?? ''), 3200);
		const fallbackContent = buildEmailFallbackContent({ file, preview });
		const content = previewText || fallbackContent || file?.name || 'Email correspondence';

		return makeSource({
			id: `sharepoint-file-${file.id}`,
			title: preview?.subject || file?.name || 'Email correspondence',
			type: 'email',
			content,
			snippet: truncate(content, 240),
			url: file?.webUrl ?? null,
			metadata: {
				fileId: file.id,
				mimeType: file?.file?.mimeType ?? null,
				from:
					preview?.senderEmail ??
					preview?.senderSmtpAddress ??
					preview?.from?.text ??
					null,
				to: getRecipientList(preview),
			},
		});
	} catch (error) {
		console.log('Unable to load Folio AI email preview', error);

		const fallbackContent = buildEmailFallbackContent({ file });

		return makeSource({
			id: `sharepoint-file-${file.id}`,
			title: file?.name || 'Email correspondence',
			type: 'email',
			content: fallbackContent,
			snippet: truncate(
				[file?.fromEmail, file?.toEmail, file?.name].filter(Boolean).join(' | '),
				240,
			),
			url: file?.webUrl ?? file?.listItem?.webUrl ?? null,
			metadata: {
				fileId: file?.id ?? null,
				mimeType: file?.file?.mimeType ?? null,
			},
		});
	}
};

const buildSharepointSources = async ({
	files = [],
	dispatch,
	sharepointItemId,
	sharepointDriveId,
}) => {
	const { emailFiles, documentFiles } = splitSharepointFiles(files);

	const documentSources = buildSharepointDocumentSources(documentFiles);

	const emailSources = await Promise.all(
		emailFiles.slice(0, 6).map((file) =>
			buildSharepointEmailSource({
				file,
				dispatch,
				sharepointItemId,
				sharepointDriveId,
			}),
		),
	);

	return [...documentSources, ...emailSources];
};

const useAskAIContextSources = ({ client, deal, objectId, contextType }) => {
	const dispatch = useDispatch();
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [contextSources, setContextSources] = useState([]);
	const [contextLoading, setContextLoading] = useState(false);

	const isClientContext = contextType === 'client';

	const sharepointItemId = isClientContext ? client?.driveId : deal?.driveId;
	const sharepointDriveId = isClientContext
		? client?.clientFolderDriveId
		: deal?.clientFolderDriveId;

	const { data: notesData } = useListNotesQuery(
		{
			filters: isClientContext ? { clientId: client?.clientId } : { dealId: deal?.dealId },
			limit: 200,
		},
		{
			skip: isClientContext ? !client?.clientId : !deal?.dealId,
		},
	);

	// The workspace belongs to the deal, or to the client in a client context.
	const workspaceDealId = isClientContext ? undefined : deal?.dealId;
	const workspaceClientId = isClientContext ? client?.clientId : undefined;

	const { data: workspacePage } = useGetWorkspacePageQuery(
		{ dealId: workspaceDealId, clientId: workspaceClientId },
		{
			skip: !workspaceDealId && !workspaceClientId,
		},
	);

	const { data: clientPolicies } = useGetClientPoliciesQuery(
		{
			insightId: client?.insightId,
			clientId: client?.clientId,
		},
		{
			skip: !isClientContext || (!client?.insightId && !client?.clientId),
		},
	);

	const { data: clientPolicyTransactionsData } = useSearchInsightPolicyTransactionsInfiniteQuery(
		{
			...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
			clientId: client?.insightId,
		},
		{
			skip: !isClientContext || !client?.insightId || !userDetails,
		},
	);

	const { data: dealPolicyData } = useGetDealPoliciesQuery(
		{ dealId: deal?.dealId },
		{
			skip: isClientContext || !deal?.dealId,
		},
	);

	const { data: sharepointFilesData } = useGetFoldersAndFilesQuery(
		{
			itemId: sharepointItemId,
			driveId: sharepointDriveId,
			recursive: true,
		},
		{
			skip: !sharepointItemId || !sharepointDriveId,
		},
	);

	const { getMessages, ...aiMessagesState } = useGetAIMessages({
		dealId: deal?.dealId,
	});

	const getAiSummaries = useCallback(
		(summaryType) => {
			const data = getMessages({ summaryType });
			return data.sort((a, b) => b.dateCreated?.seconds - a.dateCreated?.seconds);
		},
		[getMessages],
	);

	const aiSummaries = useMemo(() => {
		if (isClientContext) return {};
		return [AI_SUMMARY_TYPES.riskInfoSummary, AI_SUMMARY_TYPES.quoteSummary].reduce(
			(acc, summaryType) => {
				const data = getAiSummaries(summaryType);
				return {
					...acc,
					[summaryType]: data,
				};
			},
			{},
		);
	}, [
		aiMessagesState.isLoading,
		aiMessagesState.isUninitialized,
		aiMessagesState.isError,
		aiMessagesState.isSuccess,
	]);

	useEffect(() => {
		if (!objectId) {
			setContextSources([]);
			return;
		}

		let active = true;

		const hydrateContextSources = async () => {
			setContextLoading(true);

			try {
				const clientPolicyTransactions =
					clientPolicyTransactionsData?.pages?.flatMap((page) => page?.Results ?? []) ??
					[];

				const nextSources = [
					...buildNoteSources({
						notes: notesData?.data ?? [],
						client,
						deal,
					}),

					...(isClientContext
						? buildClientPolicySources({
								clientPolicies: clientPolicies ?? [],
								clientPolicyTransactions,
								client,
							})
						: []),

					...buildWorkspaceSources({
						workspacePage,
						contextType,
						objectId,
						client,
						deal,
					}),

					...(!isClientContext
						? buildDealAiSources({
								aiSummaries,
								deal,
							})
						: []),

					...(!isClientContext
						? buildDealPolicySources({
								policyData: dealPolicyData,
								deal,
							})
						: []),

					...(await buildSharepointSources({
						files: sharepointFilesData?.files ?? [],
						dispatch,
						sharepointItemId,
						sharepointDriveId,
					})),
				];

				if (active) {
					setContextSources(nextSources.slice(0, 30));
				}
			} finally {
				if (active) {
					setContextLoading(false);
				}
			}
		};

		hydrateContextSources();

		return () => {
			active = false;
		};
	}, [
		dispatch,
		objectId,
		contextType,
		isClientContext,
		client,
		deal,
		aiSummaries,
		notesData?.data,
		workspacePage,
		clientPolicies,
		clientPolicyTransactionsData?.pages,
		dealPolicyData,
		sharepointFilesData?.files,
		sharepointItemId,
		sharepointDriveId,
	]);

	return {
		contextSources,
		contextLoading,
	};
};

export default useAskAIContextSources;
